import { Fragment, useMemo, useRef, useState } from 'react'
import { Combobox, Dialog, Transition } from '@headlessui/react'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { UsersIcon } from '@heroicons/react/24/outline'
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import { useEffect } from 'react'
import { getNursingSearch } from '../../services/login'
import { FaSpinner } from 'react-icons/fa'
import { debounce, throttle } from 'lodash';
import ImgHolder from '../imgHolder'
import moment from 'moment'
import { useGroupChatContext } from '../../context/groupchatContext'


function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function PreviewSelectField({onSendMessage}) {

  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const recent = patients;
  const containerRef = useRef(null);

  const groupChatContext = useGroupChatContext();
  const {selectedLocations} = useMemo(() => ({
    selectedLocations: groupChatContext.selectedLocations,
  }));

  const debounceSearchPatient = async(keyword) => {

    if(keyword){
      setIsLoading(true);
      const response = await getNursingSearch(keyword, selectedLocations);
      setIsLoading(false);
      setOpen(true);
      if(response?.data?.response?.data){
        setPatients(response?.data?.response?.data)
      }
      else{
        setPatients([])
      }
    }
    else{
      setOpen(false);
    }
  }

  const searchPatient = useMemo(()=> debounce(debounceSearchPatient, 500), [selectedLocations])

  useEffect(()=>{
    if(query?.length > 2){
      searchPatient(query);
    }
  }, [query])

  const handleLeaveMouse = () => {
    setOpen(false);
    setQuery('');
  }

  const handleSendMessage = (person) => {
    onSendMessage(person);
    handleLeaveMouse();
  }

  const handleClickOutside = (event) => {
    if(containerRef.current && !containerRef.current.contains(event.target)) {
      handleLeaveMouse();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <div ref={containerRef} className="relative inset-0 z-[2]">
        <div className="mx-auto max-w-3xl transform divide-y divide-gray-100 rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
          <Combobox
            // onChange={(person) => (window.location = person.profileUrl)}
          >
            {({ activeOption }) => (
              <>
                <div className="relative">
                  {isLoading ? (
                    <FaSpinner
                      className="animate-spin pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                      size={20}
                    />
                  ) : (
                    <MagnifyingGlassIcon
                      className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  )}
                  <Combobox.Input
                    className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                    placeholder="Search Patient"
                    autoComplete="off"
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </div>
                {open ? (
                  <>
                    {(query !== "" || patients.length > 0) && (
                      <Combobox.Options
                        as="div"
                        static
                        hold
                        className="flex transform-gpu mt-[0.5px] divide-x divide-gray-100 bg-white rounded-xl shadow-2xl transition-all absolute w-full"
                      >
                        <div
                          className={classNames(
                            "max-h-96 min-w-0 flex-auto scroll-py-4 overflow-y-auto px-6 py-4",
                            activeOption && "sm:h-96"
                          )}
                        >
                          {/* {query === '' && (
                            <h2 className="mb-4 mt-2 text-xs font-semibold text-gray-500">Recent searches</h2>
                          )} */}
                          <div className="-mx-2 text-sm text-gray-700">
                            {(query === "" ? [] : patients).map((person) => (
                              <Combobox.Option
                                as="div"
                                key={person?.pid}
                                value={person}
                                className={({ active }) =>
                                  classNames(
                                    "flex cursor-default select-none items-center rounded-md mb-2 pe-2",
                                    active && "bg-gray-100 text-gray-900"
                                  )
                                }
                              >
                                {({ active }) => (
                                  <>
                                    <button className="flex items-center relative gap-2 w-full rounded-md px-2 py-2 transition-colors duration-300 hover:bg-light-gray-f5" onClick={() => {handleSendMessage(person)}}>
                                      <div className="h-[42px] w-[42px] shrink-0 rounded-full">
                                        <ImgHolder uuid={person?.file ? person?.file : person?.sex} />
                                      </div>
                                      <div className="overflow-hidden text-left flex-1">
                                        <div className='flex gap-2 justify-between'>
                                          <p className="flex-start truncate" title={person.full_name}>
                                            {person.full_name}
                                          </p>
                                        </div>
                                        <p className="text-xs text-start font-medium text-gray-500">
                                          <span className="capitalize">{person.sex}</span>, {moment(person.DOB).format('MM/DD/YYYY')} ({person.age} Y) 
                                        </p>
                                        <p className="text-xs text-start font-medium text-gray-500">
                                          MRN:  {person.mrn} 
                                        </p>
                                        <p>
                                          {person.is_aco_patient ? 
                                            <small>
                                              <span title="ACO Patient" className="aco-tag-patient-search-list">ACO</span>
                                            </small>
                                            : null
                                          }
                                          {person.is_pan_patient ? 
                                            <small>
                                              <span class="aco-tag-patient-search-list">PAN</span>
                                            </small>
                                            : null
                                          }
                                        </p>
                                        <p>
                                          <small>
                                            {person.admission_type ? 
                                              <span title={`Patient Admission Status is ${person.admission_type}`}>{person.admission_type}</span>
                                              : null
                                            }
                                            {person.admission_location_type ? 
                                              <span title={`Patient type is ${person.admission_location_type}`}> {person.admission_type ? <span>/</span> : null} {person.admission_location_type}</span>
                                              : null
                                            }
                                            {(person.admission_type == 'Wound Care' || person.admission_type == 'Psych' || person.admission_type =='Internal Medicine') ?
                                              <span title={`Patient Specialty is ${person.admission_type}`} className="aco-tag-patient-search-list">Specialty</span>
                                              : null
                                            }
                                          </small>
                                        </p>
                                        <p className="text-xs text-start font-normal text-slate-400">
                                          {person.location?.name}
                                        </p>
                                      </div>
                                      {/* {active && (
                                        <ChevronRightIcon
                                          className="ml-3 h-5 w-5 flex-none text-gray-400 float-end absolute end-0"
                                          aria-hidden="true"
                                        />
                                      )} */}
                                    </button>
                                    {/* <img src={person.imageUrl} alt="" className="h-6 w-6 flex-none rounded-full" />
                                    <span className="ml-3 flex-start truncate">{person.full_name}</span>
                                    {active && (
                                      <ChevronRightIcon
                                        className="ml-3 h-5 w-5 flex-none text-gray-400 float-end"
                                        aria-hidden="true"
                                      />
                                    )} */}
                                  </>
                                )}
                              </Combobox.Option>
                            ))}
                          </div>
                        </div>

                        {/* {activeOption && (
                          <div className="hidden h-96 w-1/2 flex-none flex-col divide-y divide-gray-100 overflow-y-auto sm:flex">
                            <div className="flex-none p-6 text-center">
                              <div className="mx-auto h-16 w-16 rounded-full">
                                <ImgHolder uuid={activeOption?.file ? activeOption?.file : activeOption?.sex} />
                              </div>
                              <h2 className="mt-3 font-semibold text-gray-900">
                                {activeOption.full_name}
                              </h2>
                              <p className="text-sm leading-6 text-gray-500">
                                {activeOption.role}
                              </p>
                            </div>
                            <div className="flex flex-auto flex-col justify-between p-6">
                              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm text-gray-700">
                                <dt className="col-end-1 font-semibold text-gray-900">
                                  Phone
                                </dt>
                                <dd>{activeOption.phone_cell}</dd>
                                <dt className="col-end-1 font-semibold text-gray-900">
                                  Email
                                </dt>
                                <dd className="truncate">
                                  <a
                                    href={`mailto:${activeOption.email}`}
                                    className="text-bg-primary underline"
                                  >
                                    {activeOption.email}
                                  </a>
                                </dd>
                              </dl>
                              <button
                                type="button"
                                className="mt-6 w-full rounded-md bg-bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bg-primary"
                                onClick={() => {handleLeaveMouse();onSendMessage(activeOption)}}
                              >
                                Send message
                              </button>
                            </div>
                          </div>
                        )} */}
                      </Combobox.Options>
                    )}

                    {query !== "" && patients.length === 0 && (
                      <div className="px-6 py-14 text-center text-sm sm:px-14 transition-all absolute w-full bg-white rounded-xl shadow-2xl">
                        <UsersIcon
                          className="mx-auto h-6 w-6 text-gray-400"
                          aria-hidden="true"
                        />
                        <p className="mt-4 font-semibold text-gray-900">
                          No patient found
                        </p>
                        <p className="mt-2 text-gray-500">
                          We couldn’t find anything with that term. Please try
                          again.
                        </p>
                      </div>
                    )}
                  </>
                ) : null}
              </>
            )}
          </Combobox>
        </div>
      </div>
    </>
  );
}
